import { Box, Checkbox, Modal, Select } from '@deity-io/portal-ui';
import { CACHE_FIRST, ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  GetAllPackageVersionsDocument,
  useCreatePackageVersionFromPreviousMutation,
  useCreatePackageVersionMutation,
  useGetPackageVersionQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

type ModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};
export const EditPublishedPackageVersionModal: FC<ModalProps> = ({ isOpen, setIsOpen }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const params = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [createNewPackageVersion] = useCreatePackageVersionMutation();
  const [createPackageVersionFromPrevious] = useCreatePackageVersionFromPreviousMutation();
  const [shouldDuplicate, setShouldDuplicate] = useState(true);
  const [shouldCreateMainVersion, setShouldCreateMainVersion] = useState(false);

  const { data: packageVersion } = useGetPackageVersionQuery({
    variables: {
      uuid: params?.versionUuid
    },
    skip: !params?.versionUuid,
    ...CACHE_FIRST
  });

  const nextMajorVersion = () => {
    const currentVersion = packageVersion?.getPackageVersion?.version;

    if (!currentVersion) {
      return '1.0';
    }

    const major = currentVersion.split('.')[0];

    return `${parseInt(major, 10) + 1}.0`;
  };

  const nextMinorVersion = () => {
    const currentVersion = packageVersion?.getPackageVersion?.version;

    if (!currentVersion) {
      return '1.0';
    }

    const parts = currentVersion.split('.');

    return `${parts[0]}.${parseInt(parts[1], 10) + 1}`;
  };

  const create = async () => {
    const newUuid = v4();
    await createNewPackageVersion({
      variables: {
        uuid: newUuid,
        packageUuid: params?.packageId,
        variables: packageVersion?.getPackageVersion?.variables,
        version: shouldCreateMainVersion ? nextMajorVersion() : nextMinorVersion(),
        createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
      },
      refetchQueries: [
        {
          query: GetAllPackageVersionsDocument,
          variables: {
            uuid: params?.packageId,
            limit: 20,
            offset: 0
          }
        }
      ]
    });

    setIsOpen(false);
    history.push(`/packages/${params?.packageId}/${newUuid}/package`);
  };

  const createAndCopy = async () => {
    const newUuid = v4();
    const settings = {
      variables: {
        previousPackageVersionUuid: packageVersion?.getPackageVersion?.uuid as string,
        newPackageVersionUuid: newUuid,
        version: shouldCreateMainVersion ? nextMajorVersion() : nextMinorVersion(),
        createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
      },
      refetchQueries: [
        {
          query: GetAllPackageVersionsDocument,
          variables: {
            packageUuid: params?.packageId,
            limit: 20,
            offset: 0
          }
        }
      ]
    };
    await createPackageVersionFromPrevious(settings);

    setIsOpen(false);
    history.push(`/packages/${params?.packageId}/${newUuid}/package`);
  };

  const createFn = shouldDuplicate ? () => createAndCopy() : () => create();

  /**
   * @todo, create check on version number already exists
   */
  return packageVersion?.getPackageVersion?.version ? (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      label="Edit deployed version"
      title="Please fill in a new version name and description"
      confirmText="Create new version"
      onConfirm={createFn}
    >
      <Box my={2}>
        <p>You&apos;ve been editing the following version:</p>

        <Select
          onChange={() => {}}
          options={[{ label: packageVersion.getPackageVersion.version, value: params.versionUuid }]}
          value={{ label: packageVersion.getPackageVersion.version, value: params.versionUuid }}
          autoSelect
          disabled
        />
        <Checkbox
          checked={shouldDuplicate}
          onChange={() => setShouldDuplicate(!shouldDuplicate)}
          label="Copy package content"
        />

        <Checkbox
          checked={shouldCreateMainVersion}
          onChange={() => setShouldCreateMainVersion(!shouldCreateMainVersion)}
          label="Create a new main-version"
        />

        <Box mt={2} px={3} py={2} bgcolor="#fffceb" borderLeft="5px solid #ecc155">
          <p>
            A new {shouldCreateMainVersion ? 'main' : 'minor'}-version will be created (
            {shouldCreateMainVersion ? nextMajorVersion() : nextMinorVersion()}) with a draft status, based on this
            version
          </p>
        </Box>
      </Box>
    </Modal>
  ) : null;
};
